import React, { useState, useEffect } from 'react';
import '../../styles/components/Admin/TranslationModal.scss';
import { translateText, translateCharacteristics } from '../../services/translationApi';

const TranslationModal = ({ isOpen, onClose, data, type, onSave }) => {
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (isOpen && data) {
      // Initialize translations with existing data
      const initial = {
        title_en: data.title_en || '',
        description_en: data.description_en || ''
      };

      // For products, include characteristics
      if (type === 'product') {
        initial.main_caracteristiques = data.main_caracteristiques || {};
        initial.secondary_caracteristiques = data.secondary_caracteristiques || {};
      }

      setTranslations(initial);
      setError(null);
    }
  }, [isOpen, data, type]);

  const handleAutoTranslate = async () => {
    if (!data.title) {
      setError('Veuillez d\'abord remplir les champs en français');
      return;
    }

    setTranslating(true);
    setError(null);

    try {
      const newTranslations = { ...translations };

      // Translate title
      if (data.title && !translations.title_en) {
        newTranslations.title_en = await translateText(data.title, 'en', 'fr');
      }

      // Translate description
      if (data.description && !translations.description_en) {
        newTranslations.description_en = await translateText(data.description, 'en', 'fr');
      }

      // Translate characteristics for products
      if (type === 'product') {
        if (data.main_caracteristiques) {
          newTranslations.main_caracteristiques = await translateCharacteristics(
            data.main_caracteristiques,
            'en',
            'fr'
          );
        }

        if (data.secondary_caracteristiques) {
          newTranslations.secondary_caracteristiques = await translateCharacteristics(
            data.secondary_caracteristiques,
            'en',
            'fr'
          );
        }
      }

      setTranslations(newTranslations);
    } catch (err) {
      setError('Erreur lors de la traduction automatique');
      console.error('Translation error:', err);
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      await onSave(translations);
      onClose();
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCharacteristicChange = (charType, key, field, value) => {
    setTranslations(prev => ({
      ...prev,
      [charType]: {
        ...prev[charType],
        [key]: {
          ...prev[charType][key],
          [field]: value
        }
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="translation-modal-overlay" onClick={onClose}>
      <div className="translation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Traduction en Anglais</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="alert-info">
            ⚠️ Vérifiez que les champs en français sont bien remplis avant de traduire
          </div>

          <button 
            className="auto-translate-btn" 
            onClick={handleAutoTranslate}
            disabled={translating}
          >
            {translating ? 'Traduction en cours...' : '🌐 Traduire automatiquement'}
          </button>

          {/* Title Translation */}
          <div className="form-group">
            <label>Titre (Français)</label>
            <input 
              type="text" 
              value={data.title || ''} 
              disabled 
              className="disabled-input"
            />
          </div>

          <div className="form-group">
            <label>Title (English)</label>
            <input 
              type="text" 
              value={translations.title_en || ''} 
              onChange={(e) => setTranslations({ ...translations, title_en: e.target.value })}
              placeholder="English title"
            />
          </div>

          {/* Description Translation (if applicable) */}
          {(type === 'product') && (
            <>
              <div className="form-group">
                <label>Description (Français)</label>
                <textarea 
                  value={data.description || ''} 
                  disabled 
                  className="disabled-input"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Description (English)</label>
                <textarea 
                  value={translations.description_en || ''} 
                  onChange={(e) => setTranslations({ ...translations, description_en: e.target.value })}
                  placeholder="English description"
                  rows="3"
                />
              </div>
            </>
          )}

          {/* Characteristics Translation (for products) */}
          {type === 'product' && (
            <>
              {/* Main Characteristics */}
              {translations.main_caracteristiques && Object.keys(translations.main_caracteristiques).length > 0 && (
                <div className="characteristics-section">
                  <h3>Caractéristiques Principales</h3>
                  {Object.entries(translations.main_caracteristiques).map(([key, char]) => (
                    <div key={key} className="characteristic-item">
                      <div className="char-row">
                        <div className="char-field">
                          <label>{char.title} (FR)</label>
                          <input type="text" value={char.title} disabled className="disabled-input" />
                        </div>
                        <div className="char-field">
                          <label>Title (EN)</label>
                          <input 
                            type="text" 
                            value={char.title_en || ''} 
                            onChange={(e) => handleCharacteristicChange('main_caracteristiques', key, 'title_en', e.target.value)}
                            placeholder="English title"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Secondary Characteristics */}
              {translations.secondary_caracteristiques && Object.keys(translations.secondary_caracteristiques).length > 0 && (
                <div className="characteristics-section">
                  <h3>Caractéristiques Secondaires</h3>
                  {Object.entries(translations.secondary_caracteristiques).map(([key, char]) => (
                    <div key={key} className="characteristic-item">
                      <div className="char-row">
                        <div className="char-field">
                          <label>{char.title} (FR)</label>
                          <input type="text" value={char.title} disabled className="disabled-input" />
                        </div>
                        <div className="char-field">
                          <label>Title (EN)</label>
                          <input 
                            type="text" 
                            value={char.title_en || ''} 
                            onChange={(e) => handleCharacteristicChange('secondary_caracteristiques', key, 'title_en', e.target.value)}
                            placeholder="English title"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={loading}>
            Annuler
          </button>
          <button className="save-btn" onClick={handleSave} disabled={loading}>
            {loading ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TranslationModal;
